import * as React from "react";
import { cn } from "@/lib/utils";

export interface TextFieldProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
}

/**
 * TextField — rebased on --surface-sunken + --border-default.
 *
 * Replaces raw <input> with inline style={{ background: '...' }} across
 * sign-in, sign-up, tune questionnaire, and any other form.
 *
 * Focus ring uses --accent (not the old coral/neon ring).
 * Error state uses --status-error.
 */
const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  ({ label, error, hint, className, id, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, "-");

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="text-xs font-medium text-[var(--text-secondary)] tracking-wide"
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          className={cn(
            "w-full",
            "bg-[var(--surface-sunken)]",
            "border border-[var(--border-default)]",
            "rounded-[var(--radius-md)]",
            "px-3 py-2.5",
            "text-sm text-[var(--text-primary)]",
            "placeholder:text-[var(--text-tertiary)]",
            "transition-colors [transition-duration:var(--duration-fast)]",
            "outline-none",
            "focus:border-[var(--accent-fill)] focus:ring-2 focus:ring-[var(--accent-subtle)]",
            error && "border-[var(--status-error)] focus:border-[var(--status-error)] focus:ring-[rgba(220,38,38,0.10)]",
            "disabled:opacity-40 disabled:cursor-not-allowed",
            className
          )}
          aria-invalid={!!error}
          aria-describedby={
            error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined
          }
          {...props}
        />
        {error && (
          <p
            id={`${inputId}-error`}
            className="text-xs text-[var(--status-error)]"
            role="alert"
          >
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={`${inputId}-hint`} className="text-xs text-[var(--text-tertiary)]">
            {hint}
          </p>
        )}
      </div>
    );
  }
);
TextField.displayName = "TextField";

export { TextField };
